/**
 * Contagem de pendências locais — SOMENTE LEITURA.
 *
 * Fonte única da definição de "pendente" para: telemetria (heartbeat), trava de
 * limpeza/logout e contador da barra superior. Não importa syncWorker (evita ciclo)
 * e lê a fila por import dinâmico (mesmo padrão de heartbeat.ts).
 *
 * "Pendente" = registro sujo (_status != 'synced') nas tabelas que o pushEnhanced
 * realmente envia + itens da fila (pending/in_progress) + eventos não enviados.
 * DLQ é reportada à parte e NÃO conta como pendente.
 */
import { Q } from '@nozbe/watermelondb'

// ESPELHO de `entityTypes` (src/sync/syncWorkerEnhanced.js ~L979) mapeado por
// SYNC_OBJECT_TO_TABLE (~L40). Se o push passar a enviar outra tabela, atualize aqui:
// o teste "drift guard" (pendingData.test.js) falha se os dois divergirem.
export const PUSHED_TABLES = [
  'farms',
  'corrals',
  'protocols',
  'batches',
  'd0s',
  'dns',
  'iatfs',
  'farms__rel__users',
  'bulls_new',
  'partida',
] as const

type CollectionsLike = { collections: { get(name: string): any } }

export type DirtyCounts = {
  total: number
  byTable: Record<string, number>
  unreadable: string[]
}

export async function countDirtyRecords(
  db: CollectionsLike,
  tables: readonly string[] = PUSHED_TABLES,
): Promise<DirtyCounts> {
  const byTable: Record<string, number> = {}
  const unreadable: string[] = []
  let total = 0
  await Promise.all(
    tables.map(async (table) => {
      try {
        const n: number = await db.collections
          .get(table)
          .query(Q.where('_status', Q.notEq('synced')))
          .fetchCount()
        byTable[table] = n
        total += n
      } catch {
        unreadable.push(table)
      }
    }),
  )
  return { total, byTable, unreadable }
}

/** Eventos de observabilidade ainda não enviados; null = não foi possível ler. */
export async function countUnsentEvents(
  db: CollectionsLike,
): Promise<number | null> {
  try {
    return await db.collections
      .get('events')
      .query(Q.where('_status', 'created'))
      .fetchCount()
  } catch {
    return null
  }
}

export type QueueCounts = {
  pending: number
  inProgress: number
  deadLetter: number
  oldestAt: number | null
}

/** Lê a fila persistente; null = não foi possível ler. */
export async function readQueueCounts(): Promise<QueueCounts | null> {
  try {
    const { syncQueue } = await import('../sync/SyncQueue')
    const s: any = await (syncQueue as any).getStats()
    return {
      pending: s?.pending ?? 0,
      inProgress: s?.inProgress ?? 0,
      deadLetter: s?.deadLetter?.total ?? 0,
      oldestAt: s?.oldestItem ?? null,
    }
  } catch {
    return null
  }
}

export type PendingSummary = {
  queuePending: number
  queueInProgress: number
  queueOldestAt: number | null
  deadLetter: number
  dirtyRecords: number
  dirtyByTable: Record<string, number>
  eventsUnsent: number
  /** Fontes que não puderam ser lidas ('queue', 'events' ou nome de tabela). */
  unreadable: string[]
  /** true se QUALQUER pendência existe OU alguma fonte não pôde ser lida (conservador). DLQ não conta. */
  hasPending: boolean
}

export function summarizePending(input: {
  queue: QueueCounts | null
  dirty: DirtyCounts
  eventsUnsent: number | null
}): PendingSummary {
  const unreadable = [...input.dirty.unreadable]
  if (input.queue == null) unreadable.push('queue')
  if (input.eventsUnsent == null) unreadable.push('events')
  const q = input.queue ?? {
    pending: 0,
    inProgress: 0,
    deadLetter: 0,
    oldestAt: null,
  }
  const events = input.eventsUnsent ?? 0
  return {
    queuePending: q.pending,
    queueInProgress: q.inProgress,
    queueOldestAt: q.oldestAt,
    deadLetter: q.deadLetter,
    dirtyRecords: input.dirty.total,
    dirtyByTable: input.dirty.byTable,
    eventsUnsent: events,
    unreadable,
    hasPending:
      q.pending + q.inProgress + input.dirty.total + events > 0 ||
      unreadable.length > 0,
  }
}

export async function getPendingSummary(
  db: CollectionsLike,
  readQueue: () => Promise<QueueCounts | null> = readQueueCounts,
): Promise<PendingSummary> {
  const [queue, dirty, eventsUnsent] = await Promise.all([
    readQueue(),
    countDirtyRecords(db),
    countUnsentEvents(db),
  ])
  return summarizePending({ queue, dirty, eventsUnsent })
}
